import React from "react";
import type { SummaryStats } from "../../types";
import type { ExportTemplateModule } from "../types";
import { formatCurrency, formatNumber } from "../utils";

interface KpiGridModuleProps {
  summary: SummaryStats;
  module: ExportTemplateModule<"kpi-grid">;
}

const KPI_COLORS = ["#38bdf8", "#34d399", "#f472b6", "#facc15"];

export const KpiGridModule: React.FC<KpiGridModuleProps> = ({
  summary,
  module,
}) => {
  const kpis = [
    {
      id: "sales",
      label: "Total Sales",
      value: formatCurrency(summary.total_sales),
      subtitle: `${summary.campaign_count} active campaigns`,
    },
    {
      id: "spend",
      label: "Media Spend",
      value: formatCurrency(summary.total_spend),
      subtitle: `${formatNumber(summary.total_impressions)} impressions`,
    },
    {
      id: "roas",
      label: "Average ROAS",
      value: `${formatNumber(summary.average_roas, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}x`,
      subtitle: `${formatNumber(summary.total_clicks)} clicks`,
    },
    {
      id: "units",
      label: "Units Sold",
      value: formatNumber(summary.total_units, {
        maximumFractionDigits: 0,
      }),
      subtitle: `${summary.brand_count} brands represented`,
    },
  ];

  return (
    <section className="w-full">
      {module.title && (
        <div className="text-slate-400 uppercase tracking-[0.2em] text-xs font-semibold mb-3">
          {module.title}
        </div>
      )}
      <div className="grid grid-cols-4 gap-4">
        {kpis.map((kpi, index) => (
          <div
            key={kpi.id}
            className="rounded-2xl bg-white shadow-[0_20px_45px_rgba(15,23,42,0.08)] border border-slate-100 p-5 flex flex-col gap-2"
          >
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: KPI_COLORS[index] }}
              />
              <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                {kpi.label}
              </span>
            </div>
            <div className="text-3xl font-semibold text-slate-900">
              {kpi.value}
            </div>
            <div className="text-sm text-slate-500">{kpi.subtitle}</div>
          </div>
        ))}
      </div>
    </section>
  );
};
